<template>
    <div>
        <template v-if="method == 'stripe-payment-intent'">
            <div class="orange-payment-method-expanded">
                <div class="orange-card-wrapper">
                    <div class="mb-2 border border-gray-300 rounded px-2">
                        <div id="orange-card-number"></div>
                    </div>
                    <div class="flex">
                        <div class="w-1/2 mr-1 border border-gray-300 rounded px-2">
                            <div id="orange-card-expiry"></div>
                        </div>
                        <div class="w-1/2 ml-1 border border-gray-300 rounded px-2">
                            <div id="orange-card-cvc"></div>
                        </div>
                    </div>
                </div>

                <div class="text-center mt-4 mb-2">
                    <button class="mt-8 mb-6 mb-2 py-2 px-6 bg-blue-600 text-white text-center rounded-full w-56 disabled:bg-gray-300 disabled:cursor-not-allowed" @click="pay" :disabled="loading">
                        <div v-if="! loading">Genomför betalning</div>
                        <div v-else class="payment-spinner">
                            <div class="payment-bounce1"></div>
                            <div class="payment-bounce2"></div>
                            <div class="payment-bounce3"></div>
                        </div>
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import {loadStripe} from '@stripe/stripe-js'
    import axios from 'axios'
    import CardRadio from "./CardRadio";
    import CardInput from "./CardInput";

    export default {
        components: {CardRadio, CardInput},
        props: ['api', 'name', 'orderId', 'method'],
        data() {
            return {
                statusTimer: null,
                loading: false,

                paymentId: null, // internal id to log stripe data

                stripe: null,
                stripePublishableKey: null,
                stripeAccount: null,
                stripeHasBooted: false,

                paymentIntent: null,
                cardNumberElement: null,
                cardExpiryElement: null,
                cardCvcElement: null,
            }
        },
        watch: {
            method: {
                handler: async function(val, oldVal) {
                    this.$nextTick(async () => {
                        if (val === 'stripe-payment-intent' && !this.stripeHasBooted) {
                            await this.bootStripe()
                            await this.mountStripe()
                            await this.createPaymentIntent()
                            this.stripeHasBooted = true
                        } else if (val === 'stripe-payment-intent') {
                            this.cardNumberElement.mount("#orange-card-number")
                            this.cardExpiryElement.mount("#orange-card-expiry")
                            this.cardCvcElement.mount("#orange-card-cvc")
                        } else if (this.cardNumberElement) {
                            this.cardNumberElement.unmount()
                            this.cardExpiryElement.unmount()
                            this.cardCvcElement.unmount()
                        }
                    })
                }
            }
        },
        methods: {
            async bootStripe() {
                let response = await axios.get(`${this.api}/stripe-payments-intent/keys`)
                this.stripe = await loadStripe(response.data.publishable_key, {
                    stripeAccount: response.data.account
                })
            },
            async mountStripe() {
                let elements = this.stripe.elements({
                    fonts: [{cssSrc: "https://rsms.me/inter/inter.css"}],
                    locale: 'sv' // 'auto'
                })

                let elementStyles = {
                    base: {
                        color: '#707070',
                        fontWeight: 600,
                        fontFamily: 'Inter, Open Sans, Segoe UI, sans-serif',
                        fontSize: '16px',
                        fontSmoothing: 'antialiased',

                        ':focus': {
                            color: '#707070',
                        },

                        '::placeholder': {
                            color: '#C1C3C9',
                        },

                        ':focus::placeholder': {
                            color: '#C1C3C9',
                        },
                    },
                    invalid: {
                        color: "#E25950"
                    },
                    complete: {
                        color: "#707070"
                    }
                }

                this.cardNumberElement = elements.create("cardNumber", { style: elementStyles })
                this.cardExpiryElement = elements.create("cardExpiry", { style: elementStyles })
                this.cardCvcElement = elements.create("cardCvc", { style: elementStyles })

                this.cardNumberElement.mount("#orange-card-number")
                this.cardExpiryElement.mount("#orange-card-expiry")
                this.cardCvcElement.mount("#orange-card-cvc")
            },
            async createPaymentIntent() {
                let response = await axios.post(`${this.api}/stripe-payments-intent/init`, { order_id: this.orderId })

                this.paymentIntent = {
                    id: response.data.intent.id,
                    client_secret: response.data.intent.client_secret,
                }

                this.paymentId = response.data.payment.id
            },
            async pay() {
                this.loading = true

                const {paymentIntent, error} = await this.stripe.confirmCardPayment(
                    this.paymentIntent.client_secret,
                    {
                        payment_method: {
                            card: this.cardNumberElement,
                            billing_details: {
                                name: this.name,
                            },
                        },
                    },
                )

                if (error) {
                    await axios.post(`${this.api}/stripe-payments-intent/attempt`, { success: false, error: error, payment_id: this.paymentId, order_id: this.orderId })
                    this.loading = false
                    alert(error.message)

                    return
                }

                await axios.post(`${this.api}/stripe-payments-intent/attempt`, { success: true, intent: paymentIntent, payment_id: this.paymentId, order_id: this.orderId })

                // Let's wait for the Stripe webhook to mark the order as paid
                this.startOrderStatusCheck()
            },
            async startOrderStatusCheck() {
                this.statusTimer = window.setInterval(() => {
                    this.checkOrderStatus()
                }, 2000)
            },
            async checkOrderStatus() {
                let response = await axios.get(`${this.api}/stripe-payments-intent/check?order_id=${this.orderId}`)

                if (response.data.paid) {
                    window.clearInterval(this.statusTimer)
                    this.$emit('success')
                } else if (response.data.error) {
                    window.clearInterval(this.statusTimer)
                }
            },
        }
    }
</script>

<style type="text/css" scoped>
    /* Stripe input */
    .orange-card-wrapper input,
    .orange-card-wrapper button {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        outline: none;
        border-style: none;
    }

    .orange-card-wrapper input:-webkit-autofill {
        -webkit-text-fill-color: #fce883;
        transition: background-color 100000000s;
        -webkit-animation: 1ms void-animation-out;
    }

    .orange-card-wrapper .StripeElement--webkit-autofill {
        background: transparent !important;
    }

    .orange-card-wrapper .StripeElement {
        width: 100%;
        padding: 11px 9px 11px 9px;
    }

    .orange-card-wrapper input {
        width: 100%;
        padding: 11px 15px 11px 0;
        color: #707070;
        background-color: transparent;
        -webkit-animation: 1ms void-animation-out;
    }

    .orange-card-wrapper input::-webkit-input-placeholder {
        color: #707070;
    }

    .orange-card-wrapper input::-moz-placeholder {
        color: #707070;
    }

    .orange-card-wrapper input:-ms-input-placeholder {
        color: #707070;
    }

    /* Spinner */
    .payment-spinner {
        width: 50px;
        text-align: center;
        margin: 0 auto;
    }

    .payment-spinner > div {
        width: 8px;
        height: 8px;
        background-color: #fff;
        margin-right: 4px;

        border-radius: 100%;
        display: inline-block;
        -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
        animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    }
    .payment-spinner.payment-spinner-blue > div {
        background-color: #1D5091;
    }

    .payment-spinner .payment-bounce1 {
        -webkit-animation-delay: -0.32s;
        animation-delay: -0.32s;
    }

    .payment-spinner .payment-bounce2 {
        -webkit-animation-delay: -0.16s;
        animation-delay: -0.16s;
    }

    @-webkit-keyframes sk-bouncedelay {
        0%, 80%, 100% { -webkit-transform: scale(0) }
        40% { -webkit-transform: scale(1.0) }
    }

    @keyframes sk-bouncedelay {
        0%, 80%, 100% {
            -webkit-transform: scale(0);
            transform: scale(0);
        } 40% {
              -webkit-transform: scale(1.0);
              transform: scale(1.0);
          }
    }
</style>